/**
 * SOLID Review
 * S — Strong. Single job: validate and hold a normalized (lowercased/trimmed) email string. All logic
 *     (format regex, length ≤320, non-empty) serves that one purpose.
 * O — Good. New validation rules are added inside create()'s guard chain without touching callers; the
 *     public surface (create/isValid/value/equals/toString) is stable.
 * L — N/A — no inheritance hierarchy (private constructor blocks subclassing entirely).
 * I — Strong. Four small methods (create/isValid/equals/toString) plus a value getter — every consumer
 *     uses the whole surface, nothing unrelated bundled in.
 * D — Strong. Zero imports; depends on nothing but the language runtime.
 */
export class EmailAddress {
  private readonly _value: string;

  private constructor(value: string) {
    this._value = value;
  }

  static create(raw: string): EmailAddress {
    if (!raw || typeof raw !== 'string') {
      throw new Error('EmailAddress: value must be a non-empty string');
    }
    const trimmed = raw.trim().toLowerCase();
    if (trimmed.length === 0) {
      throw new Error('EmailAddress: value must not be empty');
    }
    if (trimmed.length > 320) {
      throw new Error('EmailAddress: exceeds maximum length of 320 characters');
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    if (!emailRegex.test(trimmed)) {
      throw new Error(`EmailAddress: invalid email format`);
    }
    return new EmailAddress(trimmed);
  }

  static isValid(raw: string): boolean {
    try {
      EmailAddress.create(raw);
      return true;
    } catch {
      return false;
    }
  }

  get value(): string {
    return this._value;
  }

  equals(other: EmailAddress): boolean {
    return this._value === other._value;
  }

  toString(): string {
    return this._value;
  }
}
